import _ from 'underscore';
import Backbone from 'backbone';
import kendo from '@bingads-webui/kendo/kendo.core';
import { dateRangeEquals } from '@bingads-webui-universal/primitive-utilities';

const formatDateInQuery = date => kendo.toString(date, 'M/d/yyyy');
const formatDateInEntitySet = date => kendo.toString(date, 'yyyy-MM-dd');
const parseDate = dateString => kendo.parseDate(dateString);
const defaultComparisonColumns = [
  'PeriodPerformanceMetrics',
  'PerformanceMetricsChange',
  'PerformanceMetricsChangePercentage',
  'PhonePeriodPerformanceMetrics',
  'PhonePerformanceMetricsChange',
  'PhonePerformanceMetricsChangePercentage',
];

function changeEntitySet(queryParams, dateRange, edm) {
  const { options } = queryParams;
  const entitySet = edm.root.identifyEntitySet(options.entitySet.toJSON());

  const action = entitySet.navigation.source;

  entitySet.navigation.parameters.gridDataParameters = _.extend(
    {},
    entitySet.navigation.parameters.gridDataParameters, {
      DateRange: {
        StartDate: formatDateInEntitySet(dateRange.start) ||
          formatDateInEntitySet(parseDate(dateRange.RangeStartDate)),
        EndDate: formatDateInEntitySet(dateRange.end) ||
          formatDateInEntitySet(parseDate(dateRange.RangeEndDate)),
      },
      ComparisonDateRange: dateRange.compared ? {
        StartDate: formatDateInEntitySet(dateRange.compared.start),
        EndDate: formatDateInEntitySet(dateRange.compared.end),
      } : undefined,
    }
  );

  return _.defaults({
    options: _.defaults({
      entitySet: action.$call(entitySet.navigation.parameters),
    }, options),
  }, queryParams);
}

function addDateRangeToQueryParams(queryParams, dateRange) {
  let query = _.defaults({
    startDate: formatDateInQuery(dateRange.start) ||
      formatDateInQuery(parseDate(dateRange.RangeStartDate)),
    endDate: formatDateInQuery(dateRange.end) ||
      formatDateInQuery(parseDate(dateRange.RangeEndDate)),
  }, queryParams.query);

  if (dateRange.compared) {
    query = _.defaults({
      comparisonStartDate: formatDateInQuery(dateRange.compared.start),
      comparisonEndDate: formatDateInQuery(dateRange.compared.end),
    }, query);
  } else {
    query = _.omit(query, 'comparisonStartDate', 'comparisonEndDate');
  }

  return _.defaults({
    query,
  }, queryParams);
}


export const dateFilterPlugin = definePlugin => definePlugin('dateFilter', [
  'config',
  'gridView',
  'dataSource',
], (
  config,
  gridView,
  dataSource
) => {
  const events = _.extend({}, Backbone.Events);
  const pluginConfig = _.result(config.plugins, 'dateFilter', {});
  const {
    edm,
    state,
    addDateChangeListener,
    comparisonColumns = defaultComparisonColumns,
    p$daterange = () => state.getPromise('$daterange'),
  } = pluginConfig;
  const originalQuery = dataSource.query;
  let currentDateRange;

  // The default date range for newly signed up
  // user is all time which is from three years
  // ago till now
  const date = new Date();
  date.setFullYear(date.getFullYear() - 3);
  const defaultDateRange = {
    compared: null,
    start: date,
    end: new Date(),
    grain: 22,
    key: 'allTime',
    name: 'AllTime',
  };

  p$daterange().then((dateRange) => {
    currentDateRange = dateRange || defaultDateRange;
  });

  dataSource.query = (params, ...rest) => // eslint-disable-line no-param-reassign
    p$daterange().then((dateRange) => {
      const dateRangeCopy = dateRange || defaultDateRange;
      // UI next does not have addDateChangeListener
      // when date change, we will go to this query, and we use this chance to trigger
      // date change event for other projections
      currentDateRange = dateRangeCopy;
      events.trigger('dateChange');

      if (!config.isDataFetchManagerSource) {
        let queryParams;

        if (pluginConfig.isOdataActionSource) {
          const changeEntitySetFn = pluginConfig.changeEntitySet || changeEntitySet;
          queryParams = _.defaults({
            select: dateRangeCopy.compared ?
              _.union(params.select, comparisonColumns) :
              _.difference(params.select, comparisonColumns),
          }, changeEntitySetFn(params, dateRangeCopy, edm));
        } else {
          queryParams = addDateRangeToQueryParams(params, dateRangeCopy);
        }

        gridView.set({ queryParams });

        return originalQuery.call(dataSource, queryParams, ...rest);
      }

      return originalQuery.call(dataSource, params, ...rest);
    });


  addDateChangeListener((e, newDateRange) => {
    if (dateRangeEquals(_.omit(currentDateRange, 'key'), _.omit(newDateRange, 'key'))) {
      return;
    }

    currentDateRange = newDateRange;

    gridView.refresh(true);

    events.trigger('dateChange');
  });

  return {
    events,
    getCurrentDateRange() {
      return currentDateRange;
    },
    p$getCurrentDateRange() {
      return p$daterange().then(dateRange => dateRange || defaultDateRange);
    },
  };
});
